import { useState } from 'react';

const PRODUCTS = [
  {
    id: 'vpn',
    name: 'AMIK VPN',
    tagline: 'Privacy & Security',
    description:
      'A powerful VPN solution for iOS that encrypts your internet connection, hides your IP address, and keeps your online activity private. Enjoy unrestricted access to content with military-grade encryption.',
    features: ['256-bit AES Encryption', 'No-Log Policy', 'Global Server Network', 'Kill Switch Protection'],
    image:
      'https://public.readdy.ai/ai/img_res/25bee4b1c1f3a0540b5cbf9c4be18ff5.jpg',
    accent: 'from-gray-900 to-gray-800',
    tag: 'Security',
  },
  {
    id: 'cleaner',
    name: 'AMIK Cleaner',
    tagline: 'Performance & Storage',
    description:
      'Keep your iPhone running at peak performance. AMIK Cleaner intelligently identifies and removes junk files, duplicate photos, and unnecessary data to free up valuable storage space.',
    features: ['Smart Junk Detection', 'Duplicate Photo Finder', 'Memory Optimizer', 'Battery Health Monitor'],
    image:
      'https://readdy.ai/api/search-image?query=iOS%20cleaner%20app%20concept%20art%2C%20warm%20beige%20and%20sand%20background%2C%20circular%20progress%20ring%20with%20sparkle%20effects%2C%20clean%20storage%20optimization%20visualization%2C%20soft%20warm%20lighting%2C%20minimal%20abstract%20design%2C%20no%20text%2C%20elegant%20composition&width=600&height=700&seq=amik-cleaner-product&orientation=portrait',
    accent: 'from-stone-100 to-amber-50',
    tag: 'Performance',
    dark: false,
  },
];

export default function ProductsSection() {
  const [active, setActive] = useState<string | null>(null);

  return (
    <section id="products" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14">
          <p className="text-xs font-semibold tracking-widest text-emerald-600 uppercase mb-3">Our Products</p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Designed to Protect<br />
            <span className="italic font-light text-gray-400">and Optimize</span>
          </h2>
          <p className="text-gray-500 mt-4 text-base max-w-lg">
            Two powerful iOS applications built to enhance your security and keep your device running smoothly.
          </p>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PRODUCTS.map((product) => {
            const isDark = product.id === 'vpn';
            return (
              <div
                key={product.id}
                className={`relative rounded-3xl overflow-hidden cursor-pointer transition-transform duration-300 hover:-translate-y-1 ${
                  isDark ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-stone-100 to-amber-50'
                }`}
                onMouseEnter={() => setActive(product.id)}
                onMouseLeave={() => setActive(null)}
              >
                {/* Image */}
                <div className="w-full h-72 md:h-80 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </div>

                {/* Content */}
                <div className="p-7">
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full ${
                        isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-200/60 text-amber-700'
                      }`}
                    >
                      {product.tag}
                    </span>
                  </div>
                  <h3 className={`text-2xl font-extrabold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {product.name}
                  </h3>
                  <p className={`text-sm font-medium mb-4 ${isDark ? 'text-emerald-400' : 'text-amber-600'}`}>
                    {product.tagline}
                  </p>
                  <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {product.description}
                  </p>

                  {/* Feature list */}
                  <ul className="grid grid-cols-2 gap-2">
                    {product.features.map((feat) => (
                      <li key={feat} className="flex items-center gap-2">
                        <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-amber-600'}`}>
                          <i className="ri-check-line text-sm" />
                        </span>
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
